import { Link } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { usePendingApprovals } from "@/lib/api-hooks";
import { cn } from "@/lib/utils";

function formatAgo(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now.getTime() - t);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

export function PendingApprovalsPanel(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error } = usePendingApprovals();

  const items = data?.data.slice(0, 3) ?? [];
  const total = data?.total ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-semibold">
          {t("dashboard.pending.title", { count: total })}
        </CardTitle>
        <Link
          to="/approvals"
          className="text-xs font-medium text-primary hover:underline"
        >
          {t("dashboard.pending.see_all")} →
        </Link>
      </CardHeader>
      <CardContent className="space-y-1.5 pb-3">
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {error && !isLoading && (
          <div className="text-xs text-muted-foreground">
            {t("dashboard.pending.unavailable")}
          </div>
        )}

        {!isLoading && !error && items.length === 0 && (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <CheckCircle2 className="size-4 text-success" />
            <span>{t("dashboard.pending.empty")}</span>
          </div>
        )}

        {items.map((item) => (
          <Link
            key={item.id}
            to="/approvals"
            className={cn(
              "flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm",
              "transition-colors hover:bg-muted/60",
            )}
          >
            <AlertCircle
              className={cn(
                "size-4 shrink-0",
                item.for_me ? "text-destructive" : "text-warning",
              )}
            />
            <div className="flex-1 truncate">
              <code className="font-mono text-xs">{item.action_type}</code>
              {item.target_id && (
                <span className="ml-1.5 truncate text-muted-foreground">
                  · {item.target_id.slice(0, 8)}
                </span>
              )}
            </div>
            <span className="hidden whitespace-nowrap text-xs text-muted-foreground sm:inline">
              {formatAgo(item.created_at)}
            </span>
            {item.for_me && (
              <span className="rounded-sm bg-destructive/10 px-1.5 text-[10px] font-semibold uppercase text-destructive">
                {t("dashboard.pending.you")}
              </span>
            )}
            <ArrowRight className="size-4 text-muted-foreground" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}
